// Pure helpers for the top bar's notification bell. Moved over unchanged from the old bell
// (src/components/notifications/NotificationBell.tsx), which is deleted with Mantine in the cleanup PR.
// Type-only import: this module stays free of the hook's axios client, so its test runs bare.
import type { EmailResponseNotificationItem } from '@hooks/notifications/use-email-response-notifications';

/**
 * CEISA AJU numbers are stored unpunctuated but always read as
 * 000020-029326-20251017-005630. Anything that is not that length is shown
 * untouched rather than mangled into a wrong grouping.
 */
export const formatNomorAju = (nomorAju: string): string => {
  if (!nomorAju) return '';
  const clean = nomorAju.replace(/-/g, '');
  if (clean.length < 26) return nomorAju;
  return `${clean.slice(0, 6)}-${clean.slice(6, 12)}-${clean.slice(12, 20)}-${clean.slice(20, 26)}`;
};

/** The last six characters of an AJU, the short form a shipment is referred to by (the email's REF). */
export const formatAjuShortRef = (nomorAju: string): string => {
  const clean = (nomorAju || '').replace(/-/g, '');
  return clean.length >= 6 ? clean.slice(-6) : clean;
};

/**
 * The response's own timestamp, as "27 Agu 2026, 14:32": an exact time, not an age. `at` is
 * `parseResponseTime(item)` from the notifications hook.
 */
export const formatResponDateTime = (at: number | null, waktuRespon: string): string => {
  if (at === null) return waktuRespon || '';

  const parsed = new Date(at);
  const date = parsed.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = parsed.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

export const formatWaktuRespon = (waktuRespon: string): string => {
  if (!waktuRespon) return '';
  const parsed = new Date(waktuRespon);
  if (Number.isNaN(parsed.getTime())) return waktuRespon;

  return parsed.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

/**
 * How long ago the newest response landed, as hh:mm. Hours are not wrapped at 24: "38:20" is the
 * honest reading for a queue nobody has looked at since yesterday.
 */
export const formatElapsed = (since: Date | null, now = Date.now()): string | null => {
  if (!since) return null;

  const totalMinutes = Math.floor((now - since.getTime()) / 60000);
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return null;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export type ResponseTone = 'err' | 'ok' | 'warn' | 'info';

/**
 * Tone follows the response's meaning, matching the CEISA popups. Urgency is decided by the server
 * (`isUrgent`), so the badge and the glyph cannot disagree about the same response.
 */
export const responseTone = (
  notification: Pick<EmailResponseNotificationItem, 'isUrgent' | 'uraianSingkatRespon'>
): ResponseTone => {
  if (notification.isUrgent) return 'err';
  const upper = (notification.uraianSingkatRespon || '').toUpperCase();
  if (upper.includes('SPJM')) return 'err';
  if (upper.includes('SPPB') || upper.includes('NPE')) return 'ok';
  if (upper.includes('BILLING')) return 'warn';
  return 'info';
};

/** Badge text for a count: the bell caps at 99+, the changelog at 9+. */
export const capCount = (count: number, cap: number): string => (count > cap ? `${cap}+` : String(count));
